<template>
  <div
    class="flex flex-col gap-4 bg-white rounded-xl shadow-sm p-4 md:p-6 text-left w-full"
    :class="[
      active ? 'border-2 border-primary30' : 'border border-gray-200',
      selectable ? 'cursor-pointer' : ''
    ]"
    @click="selectable ? emit('select') : undefined"
  >
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2 flex-wrap">
        <div
          class="flex items-center bg-gray-50 rounded-lg border border-gray-100 p-1 pr-3 shadow-sm"
        >
          <div class="flex flex-col px-2">
            <span class="text-base md:text-xl font-bold text-gray-700 leading-tight">
              #{{ stationId }}
            </span>
          </div>
        </div>
        <span class="font-bold text-lg md:text-2xl text-textPrimary">{{ title }}</span>
      </div>

      <span v-if="breadcrumb" class="text-xs md:text-sm text-primary50 break-words">
        {{ breadcrumb }}
      </span>
    </div>

    <div v-if="allocatedBy" class="flex gap-2 text-sm">
      <span class="font-bold text-gray-500">Alocado por:</span>
      <span>{{ allocatedBy }}</span>
    </div>

    <div v-if="startDate || endDate" class="flex flex-col text-sm leading-relaxed">
      <span v-if="startDate">
        De
        <span class="font-bold">{{ startDate.weekday }},</span>
        {{ startDate.date }}
        <span class="font-bold">às {{ startDate.time }}</span>
      </span>
      <span v-if="endDate">
        Até
        <span class="font-bold">{{ endDate.weekday }},</span>
        {{ endDate.date }}
        <span class="font-bold">às {{ endDate.time }}</span>
      </span>
      <span v-if="timezone" class="text-xs text-gray-500 mt-0.5">Horário de {{ timezone }}</span>
    </div>

    <div
      v-if="doctor"
      class="flex flex-col md:flex-row flex-1 relative justify-center p-3 gap-4 md:gap-8 py-4 px-4 md:px-6 border border-gray-200 rounded-lg shadow-sm bg-white text-sm leading-relaxed text-left"
    >
      <span
        :class="[
          'absolute top-2 left-2 text-xs text-gray-600 border px-1 py-0.5 rounded',
          dutyBgColor
        ]"
      >
        {{ capitalizedDutyLabel }}
      </span>

      <div class="flex flex-col items-center justify-center w-full md:w-32">
        <img
          src="@/assets/icons/doctorAvatar.svg"
          alt="Avatar do médico"
          class="w-10 h-10 rounded-full object-cover"
        />
        <p class="text-lg mt-1 text-center">{{ doctorDisplayName }}</p>
        <p>
          <strong>{{ doctor.crm }}</strong>
        </p>
      </div>

      <div class="hidden md:block w-px bg-gray-200 h-auto self-stretch"></div>
      <div class="block md:hidden w-full h-px bg-gray-200 self-stretch"></div>

      <div class="flex flex-1 flex-col gap-1 text-center justify-center md:text-left">
        <p class="text-base">
          <strong>Formação:</strong>
          {{ doctor.formation?.trim().length ? doctor.formation : 'Não informado' }}
        </p>
        <p class="text-base">
          <strong>Equipe:</strong>
          {{ doctor.team?.trim().length ? doctor.team : 'Não informado' }}
        </p>
        <p class="text-base">
          <strong>Posição:</strong>
          {{ doctor.position?.trim().length ? doctor.position : 'Não informado' }}
        </p>
        <p class="text-base">
          <strong>Turno:</strong>
          {{ doctor.shift?.trim().length ? capitalizeFirstLetter(doctor.shift) : 'Não informado' }}
        </p>
      </div>
    </div>

    <ObservationList v-if="observations?.length" :observations="observations" />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import { capitalizeFirstLetter } from '@/utils/string'
import { getDutyTagBgClass, getDutyTagBorderClass } from '@/utils/doctorStation'
import ObservationList from '@/components/ObservationList.vue'

export interface BestCandidateDoctor {
  name: string
  crm: string
  formation: string
  team: string
  position: string
  shift: string
  dutyLabel: string
}

export interface BestCandidateDateInfo {
  weekday: string
  date: string
  time: string
}

export interface BestCandidateObservation {
  author: string
  timestamp: string
  text: string
}

const props = withDefaults(
  defineProps<{
    stationId: number | string
    stationType?: string
    title?: string
    breadcrumb?: string
    allocatedBy?: string
    startDate?: BestCandidateDateInfo
    endDate?: BestCandidateDateInfo
    timezone?: string
    doctor?: BestCandidateDoctor
    observations?: BestCandidateObservation[]
    active?: boolean
    selectable?: boolean
  }>(),
  {
    title: 'Detalhes do Plantão',
    active: false,
    selectable: false
  }
)

const emit = defineEmits<{ select: [] }>()

const doctorDisplayName = computed(() => getFirstAndSecondName(props.doctor?.name ?? ''))

const dutySource = computed(() => props.stationType ?? props.doctor?.dutyLabel ?? '')

const capitalizedDutyLabel = computed(() =>
  dutySource.value ? capitalizeFirstLetter(dutySource.value) : ''
)

const dutyBgColor = computed(() => {
  return `${getDutyTagBgClass(dutySource.value)} ${getDutyTagBorderClass(dutySource.value)}`
})
</script>
